import React, {useMemo, useRef} from "react";
import {Table} from "reactstrap";
import HelpIcon from "../Help/HelpIcon";
import {Line} from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  Filler,
  TimeScale,
  ChartOptions,
  ChartData
} from "chart.js";
import "chartjs-adapter-date-fns";
import annotationPlugin from "chartjs-plugin-annotation";

import {chartHeight, fpsAxisDefaultMax} from "../../constants";
import colors from "../../styles/colors";
import {useChartTheme} from "../../contexts/ChartThemeContext";
import {ChartErrorBoundary} from "../Charts/ChartErrorBoundary";
import {createTickFormatter} from "../../utils/chartTicks";
import {BackgroundColorContext} from "../../contexts/BackgroundColorContext";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  ChartLegend,
  Filler,
  TimeScale,
  annotationPlugin
);

interface FpsReportProps {
  points: Array<[number, number]>; // [time, fps]
  profile: string;
  resolution: string;
  pc1: number;
  avg: number;
  max: number;
  min: number;
  stdev: number;
  axisSeries?: {
    name: string;
    data: Array<{timestamp: number; value: number}>;
    timeRange?: {start: number; end: number};
    ticks?: Array<{timestamp: number; label: string}>;
  };
}

const FpsReport: React.FC<FpsReportProps> = (props) => {
  const chartRef = useRef<ChartJS<"line", {x: number; y: number}[], unknown>>(null);
  const {getThemeColors} = useChartTheme();

  if (!props.points || !props.points.length) {
    return <></>;
  }

  // Create tick formatter with consistent formatting
  // Note: Data is pre-localized in parent ChartsReport, so we pass 0 for timezone offset
  const tickFormatter = useMemo(() => {
    return createTickFormatter(0);
  }, []);

  const chartData = useMemo(() => {
    if (!props.points || props.points.length === 0) {
      return [];
    }

    // If we have axisSeries, use it for proper smoothing
    if (props.axisSeries?.data) {
      try {
        // Get the actual data range from FPS points
        const dataStart = Math.min(...props.points.map((p) => p[0]));
        const dataEnd = Math.max(...props.points.map((p) => p[0]));

        // Create a timeline that covers the actual data range
        const dataTimeline = props.axisSeries.data.filter(
          (point) => point.timestamp >= dataStart && point.timestamp <= dataEnd
        );

        // Create a proper smoothing function for sparse data
        const createSmoothedData = (dataPoints: Array<[number, number]>, timeline: any[]) => {
          // First, interpolate FPS values for all timeline points
          const interpolatedData = timeline.map((timelinePoint) => {
            const timestamp = timelinePoint.timestamp;

            // Find the two closest data points (before and after)
            let beforePoint = null;
            let afterPoint = null;

            for (const point of dataPoints) {
              if (point[0] <= timestamp) {
                if (!beforePoint || point[0] > beforePoint[0]) {
                  beforePoint = point;
                }
              }
              if (point[0] >= timestamp) {
                if (!afterPoint || point[0] < afterPoint[0]) {
                  afterPoint = point;
                }
              }
            }

            let interpolatedValue = null;

            if (beforePoint && afterPoint && beforePoint[0] !== afterPoint[0]) {
              // Linear interpolation between two points
              const ratio = (timestamp - beforePoint[0]) / (afterPoint[0] - beforePoint[0]);
              interpolatedValue = beforePoint[1] + ratio * (afterPoint[1] - beforePoint[1]);
            } else if (beforePoint) {
              interpolatedValue = beforePoint[1];
            } else if (afterPoint) {
              interpolatedValue = afterPoint[1];
            }

            return {timestamp, value: interpolatedValue};
          });

          // Now apply smoothing to the interpolated data
          const smoothingWindow = 7; // Smooth over 7 timeline points

          return interpolatedData.map((point, index) => {
            if (point.value === null) {
              return point;
            }

            const start = Math.max(0, index - Math.floor(smoothingWindow / 2));
            const end = Math.min(interpolatedData.length, index + Math.floor(smoothingWindow / 2) + 1);

            const windowPoints = interpolatedData.slice(start, end).filter((p) => p.value !== null);

            if (windowPoints.length === 0) {
              return point;
            }

            // Simple average of nearby interpolated points
            const average = windowPoints.reduce((sum, p) => sum + (p.value || 0), 0) / windowPoints.length;

            return {timestamp: point.timestamp, value: average};
          });
        };

        // Create smoothed data
        const smoothedFpsData = createSmoothedData(props.points, dataTimeline);

        // Now create chart data using the timeline
        const chartFormatData = dataTimeline.map((timelinePoint, index) => {
          const timestamp = timelinePoint.timestamp;

          // Find closest raw FPS point
          let closestRaw = null;
          let minRawDistance = Infinity;

          for (const rawPoint of props.points) {
            const distance = Math.abs(rawPoint[0] - timestamp);
            if (distance < minRawDistance) {
              minRawDistance = distance;
              closestRaw = rawPoint;
            }
          }

          // Get smoothed value from our smoothed data
          const smoothedValue = smoothedFpsData[index]?.value || null;

          return {
            time: timestamp,
            timestamp: timestamp,
            fps: closestRaw && minRawDistance <= 5000 ? closestRaw[1] : null,
            smoothedFps: smoothedValue,
            hasData: (closestRaw && minRawDistance <= 5000) || smoothedValue !== null
          };
        });

        return chartFormatData;
      } catch {
        // Fall back to original method
      }
    }

    // Fallback: Create smoothed data manually using a simple moving average
    const windowSizeMs = 3000; // 3 seconds

    const processedData = props.points.map((point) => {
      const [timestamp, fps] = point;

      // Calculate 3-second moving average for smoothed FPS
      const windowStart = timestamp - windowSizeMs / 2;
      const windowEnd = timestamp + windowSizeMs / 2;

      const pointsInWindow = props.points.filter((p) => p[0] >= windowStart && p[0] <= windowEnd);

      const smoothedFps =
        pointsInWindow.length > 0 ? pointsInWindow.reduce((sum, p) => sum + p[1], 0) / pointsInWindow.length : fps;

      return {
        time: timestamp,
        timestamp: timestamp,
        fps: fps, // Raw FPS value
        smoothedFps: smoothedFps, // 3-second averaged FPS
        hasData: true
      };
    });

    return processedData;
  }, [props.points, props.axisSeries]);

  const bestThreshold: number = 60;
  const betterThreshold: number = 45;
  const themeColors = getThemeColors();

  // Prepare data for Chart.js
  const preparedChartData = useMemo(() => {
    // Separate raw FPS and smoothed FPS data
    const rawFpsData = chartData
      .filter((d) => d.fps !== null && d.fps !== undefined)
      .map((d) => ({x: d.time, y: d.fps as number}));

    const smoothedFpsData = chartData
      .filter((d) => d.smoothedFps !== null && d.smoothedFps !== undefined)
      .map((d) => ({x: d.time, y: d.smoothedFps as number}));

    return {
      rawFps: rawFpsData,
      smoothedFps: smoothedFpsData
    };
  }, [chartData]);

  // Chart.js data configuration
  const data: ChartData<"line", {x: number; y: number}[]> = {
    datasets: [
      {
        label: "Smoothed FPS",
        data: preparedChartData.smoothedFps,
        borderColor: colors.brand, // Solid color for the line stroke
        // Hex colors use #RRGGBBAA format where AA is the alpha channel (transparency)
        // '80' in hex = 128 in decimal = 50% opacity (128/255 = 0.50)
        // This creates a semi-transparent fill under the line so you can see through it
        // - 'FF' = 100% opaque (fully solid)
        // - 'CC' = 80% opaque (slightly transparent)
        // - '80' = 50% opaque (half transparent)
        // - '40' = 25% opaque (mostly transparent)
        // - '00' = 0% opaque (fully transparent)
        backgroundColor: colors.brand + "DD", // e.g., #7DCEA0 becomes #7DCEA080
        fill: true,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.4
      },
      {
        label: "Raw FPS",
        data: preparedChartData.rawFps,
        // 'CC' in hex = 204 in decimal = 80% opacity (204/255 = 0.80)
        // Makes dots mostly solid but slightly transparent for layering
        backgroundColor: colors.orange + "CC", // e.g., #FF6B35 becomes #FF6B35CC
        borderColor: colors.orange, // Solid color for dot borders
        pointRadius: 2,
        pointHoverRadius: 4,
        showLine: false // This makes it behave like scatter
      }
    ]
  };

  // Chart.js options configuration
  const options: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false, // Disable animations for better performance
      interaction: {
        mode: "index" as const,
        intersect: false
      },
      plugins: {
        legend: {
          display: true,
          position: "top" as const,
          labels: {
            color: themeColors.text,
            usePointStyle: true,
            padding: 15
          }
        },
        tooltip: {
          enabled: true,
          backgroundColor: themeColors.tooltip.background,
          borderColor: themeColors.tooltip.border,
          borderWidth: 1,
          titleColor: themeColors.tooltip.text,
          bodyColor: themeColors.tooltip.text,
          padding: 12,
          displayColors: true,
          callbacks: {
            title: (context) => {
              const timestamp = context[0]?.parsed?.x;
              return timestamp ? new Date(timestamp).toLocaleTimeString() : "";
            },
            label: (context) => {
              const label = context.dataset.label || "";
              const value = context.parsed?.y;
              return value !== null && value !== undefined ? `${label}: ${Math.round(value)} FPS` : `${label}: No data`;
            }
          }
        },
        annotation: {
          annotations: {
            line60: {
              type: "line" as const,
              scaleID: "y",
              value: 60,
              borderColor: colors.green,
              borderWidth: 2,
              borderDash: [5, 5],
              label: {
                display: true,
                content: "60 FPS",
                position: "end" as const,
                backgroundColor: colors.green,
                color: "white",
                font: {
                  size: 12,
                  weight: "bold" as const
                },
                padding: 4
              }
            },
            lineAvg: {
              type: "line" as const,
              scaleID: "y",
              value: props.avg,
              borderColor: colors.yellow,
              borderWidth: 2,
              borderDash: [5, 5],
              label: {
                display: true,
                content: `Avg (${Math.round(props.avg)})`,
                position: "end" as const,
                backgroundColor: colors.yellow,
                color: "black",
                font: {
                  size: 12,
                  weight: "bold" as const
                },
                padding: 4
              }
            },
            lineP1: {
              type: "line" as const,
              scaleID: "y",
              value: props.pc1,
              borderColor: colors.fiestared,
              borderWidth: 2,
              borderDash: [5, 5],
              label: {
                display: true,
                content: `P1 (${Math.round(props.pc1)})`,
                position: "end" as const,
                backgroundColor: colors.fiestared,
                color: "white",
                font: {
                  size: 12,
                  weight: "bold" as const
                },
                padding: 4
              }
            }
          }
        }
      },
      scales: {
        x: {
          type: "linear",
          display: true,
          title: {
            display: false
          },
          grid: {
            display: true,
            color: themeColors.grid + "30",
            drawOnChartArea: true,
            drawTicks: true
          },
          ticks: {
            color: themeColors.text,
            font: {
              size: 11
            },
            maxTicksLimit: 20, // Increase from default (~10) to show more ticks
            autoSkip: true,
            autoSkipPadding: 10, // Reduce padding between ticks
            callback: function (value) {
              return tickFormatter(value);
            }
          },
          min: props.axisSeries?.timeRange?.start,
          max: props.axisSeries?.timeRange?.end
        },
        y: {
          type: "linear" as const,
          display: true,
          position: "left" as const,
          title: {
            display: true,
            text: "FPS",
            color: themeColors.text
          },
          grid: {
            display: true,
            color: themeColors.grid + "30",
            drawOnChartArea: true,
            drawTicks: true
          },
          ticks: {
            color: themeColors.text,
            font: {
              size: 11
            }
          },
          min: props.min,
          max: Math.max(fpsAxisDefaultMax, props.max)
        }
      }
    }),
    [themeColors, props.avg, props.pc1, props.min, props.max, props.axisSeries, tickFormatter, preparedChartData]
  );

  return (
    <BackgroundColorContext.Consumer>
      {() => (
        <>
          <h5>Graphics Performance</h5>

          <Table responsive>
            <thead>
              <tr>
                <th style={{width: "20%"}}>
                  Profile
                  <HelpIcon topicId="profile" />
                </th>
                <th style={{width: "16%"}}>
                  Resolution
                  <HelpIcon topicId="resolution" />
                </th>
                <th style={{width: "16%"}}>
                  P<sub>1</sub>
                  <HelpIcon topicId="percentile" />
                </th>
                <th style={{width: "16%"}}>Avg</th>
                <th style={{width: "16%"}}>Max</th>
                <th style={{width: "16%"}}>
                  Std Dev
                  <HelpIcon topicId="stdev" />
                </th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>
                  <div className="numbers neutral">{props.profile}</div>
                </td>
                <td>
                  <div className="numbers neutral">{props.resolution}</div>
                </td>
                <td>
                  <div
                    className={`numbers ${
                      props.pc1 >= bestThreshold ? "good" : props.pc1 >= betterThreshold ? "medium" : "bad"
                    }`}
                  >
                    {props.pc1}
                  </div>
                </td>
                <td>
                  <div
                    className={`numbers ${
                      props.avg >= bestThreshold ? "good" : props.avg >= betterThreshold ? "medium" : "bad"
                    }`}
                  >
                    {props.avg}
                  </div>
                </td>
                <td>
                  <div
                    className={`numbers ${
                      props.max >= bestThreshold ? "good" : props.max >= betterThreshold ? "medium" : "bad"
                    }`}
                  >
                    {props.max}
                  </div>
                </td>

                <td>
                  <div className="numbers neutral">{props.stdev}</div>
                </td>
              </tr>
            </tbody>
          </Table>

          <ChartErrorBoundary>
            <div
              style={{
                width: "100%",
                marginTop: "20px",
                marginBottom: "30px",
                position: "relative"
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: "10px"
                }}
              ></div>
              <div
                style={{
                  width: "100%",
                  height: chartHeight * 1.25,
                  position: "relative"
                }}
              >
                <Line ref={chartRef} data={data} options={options} />
              </div>
            </div>
          </ChartErrorBoundary>
        </>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default FpsReport;
